import { useCallback, useEffect, useRef, useState } from "react";
import type { PowerSample, TrainerInfo } from "@cimara/trainer";

type TrainerModule = typeof import("@cimara/trainer");

export type TrainerStatus =
  | "unavailable"
  | "idle"
  | "connecting"
  | "connected"
  | "reconnecting"
  | "error";

export interface UseTrainerOptions {
  onPower?: (sample: PowerSample) => void;
  onDisconnect?: () => void;
}

export interface TrainerController {
  status: TrainerStatus;
  info: TrainerInfo | null;
  error: string | null;
  hasBluetooth: boolean;
  connected: boolean;
  connect(): Promise<void>;
  disconnect(): Promise<void>;
  applyGrade(gradePct: number): void;
  clearError(): void;
}

let trainerModulePromise: Promise<TrainerModule> | null = null;

/**
 * The BLE module is loaded lazily. The promise is also warmed when the ride
 * mounts so the click still has user activation when requestDevice runs.
 */
function loadTrainerModule(): Promise<TrainerModule> {
  trainerModulePromise ??= import("@cimara/trainer");
  return trainerModulePromise;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function bluetoothAvailable(): boolean {
  return typeof navigator !== "undefined" && "bluetooth" in navigator;
}

export function useTrainer(options: UseTrainerOptions = {}): TrainerController {
  const [status, setStatus] = useState<TrainerStatus>(() =>
    bluetoothAvailable() ? "idle" : "unavailable",
  );
  const [info, setInfo] = useState<TrainerInfo | null>(null);
  const [error, setError] = useState<string | null>(null);

  const trainerRef = useRef<import("@cimara/trainer").BleTrainer | null>(null);
  const connectedRef = useRef(false);
  const unmountedRef = useRef(false);
  const lastGradeRef = useRef(Number.NaN);
  const unsubscribePowerRef = useRef<(() => void) | null>(null);
  const unsubscribeErrorRef = useRef<(() => void) | null>(null);
  const unsubscribeConnectionRef = useRef<(() => void) | null>(null);
  const onPowerRef = useRef(options.onPower);
  const onDisconnectRef = useRef(options.onDisconnect);

  onPowerRef.current = options.onPower;
  onDisconnectRef.current = options.onDisconnect;

  useEffect(() => {
    unmountedRef.current = false;
    // Warm the module while the rider is still on the start screen. The click
    // itself can then stay within the browser's user-activation window.
    void loadTrainerModule().catch(() => {
      // The connect path reports an actionable error if this is fatal.
    });

    return () => {
      unmountedRef.current = true;
      unsubscribePowerRef.current?.();
      unsubscribeErrorRef.current?.();
      unsubscribeConnectionRef.current?.();
      unsubscribePowerRef.current = null;
      unsubscribeErrorRef.current = null;
      unsubscribeConnectionRef.current = null;
      const trainer = trainerRef.current;
      trainerRef.current = null;
      connectedRef.current = false;
      if (trainer) {
        void trainer.disconnect().catch(() => {
          // Best-effort cleanup.
        });
      }
    };
  }, []);

  const connect = useCallback(async (): Promise<void> => {
    if (trainerRef.current) return;
    if (!bluetoothAvailable()) {
      setStatus("unavailable");
      setError(
        "Web Bluetooth is not available in this browser. Sim power still drives the ride — use Chrome or Edge on localhost or HTTPS to pair a trainer.",
      );
      return;
    }

    setStatus("connecting");
    setError(null);

    try {
      const trainerModule = await loadTrainerModule();
      if (unmountedRef.current) return;

      const trainer = new trainerModule.BleTrainer({
        logger: (message, cause) => {
          if (cause) console.debug(`[trainer] ${message}`, cause);
          else console.debug(`[trainer] ${message}`);
        },
      });
      trainerRef.current = trainer;

      unsubscribePowerRef.current = trainer.onPower((sample) => {
        onPowerRef.current?.(sample);
      });
      unsubscribeErrorRef.current = trainer.onError((trainerError) => {
        setError(trainerError.message);
      });
      unsubscribeConnectionRef.current = trainer.onConnection((event) => {
        connectedRef.current = event.connected;
        if (event.connected) {
          lastGradeRef.current = Number.NaN;
          setStatus("connected");
          setInfo(trainer.getInfo());
          setError(null);
        } else {
          onDisconnectRef.current?.();
          if (event.reason === "reconnect-failed") {
            setStatus("error");
            setError(
              trainer.getLastError()?.message ??
                "Could not reconnect to the trainer. Close other Bluetooth apps and try again.",
            );
          } else if (event.reason === "disconnected") {
            setStatus("reconnecting");
          } else {
            setStatus("idle");
          }
        }
      });

      const trainerInfo = await trainer.connect();
      if (unmountedRef.current) {
        void trainer.disconnect().catch(() => {});
        return;
      }
      connectedRef.current = true;
      setInfo(trainerInfo);
      setStatus("connected");
      setError(null);
    } catch (cause) {
      connectedRef.current = false;
      setStatus("error");
      setError(errorMessage(cause));
      const trainer = trainerRef.current;
      trainerRef.current = null;
      unsubscribePowerRef.current?.();
      unsubscribeErrorRef.current?.();
      unsubscribeConnectionRef.current?.();
      unsubscribePowerRef.current = null;
      unsubscribeErrorRef.current = null;
      unsubscribeConnectionRef.current = null;
      if (trainer) {
        void trainer.disconnect().catch(() => {});
      }
    }
  }, []);

  const disconnect = useCallback(async (): Promise<void> => {
    const trainer = trainerRef.current;
    trainerRef.current = null;
    connectedRef.current = false;
    setInfo(null);
    setStatus("idle");
    unsubscribePowerRef.current?.();
    unsubscribeErrorRef.current?.();
    unsubscribeConnectionRef.current?.();
    unsubscribePowerRef.current = null;
    unsubscribeErrorRef.current = null;
    unsubscribeConnectionRef.current = null;
    if (trainer) {
      try {
        await trainer.disconnect();
      } catch (cause) {
        setError(errorMessage(cause));
      }
    }
  }, []);

  const applyGrade = useCallback((gradePct: number): void => {
    const trainer = trainerRef.current;
    if (!trainer || !connectedRef.current || !Number.isFinite(gradePct)) return;
    if (Math.abs(gradePct - lastGradeRef.current) < 0.15) return;
    lastGradeRef.current = gradePct;
    void trainer.setGrade(gradePct).catch((cause) => {
      // A control failure must never stop the power stream; show it softly.
      setError(errorMessage(cause));
    });
  }, []);

  const clearError = useCallback(() => setError(null), []);

  return {
    status,
    info,
    error,
    hasBluetooth: bluetoothAvailable(),
    connected: status === "connected",
    connect,
    disconnect,
    applyGrade,
    clearError,
  };
}
